import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { colors } from '../../brand-colors';

const ASSET_TYPE_LABELS = {
  social_post: 'Social Posts',
  long_form: 'Blog Posts',
  short_video: 'Video Scripts',
  repost: 'Reposts',
  curated: 'Curated',
};

function WeeklyReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await fetch('/api/weekly-assets/report?weeks=8', { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to load report');
        setData(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  if (loading) return <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.goldMid }} /></div>;
  if (error) return <Alert variant="danger" className="m-4">{error}</Alert>;
  if (!data) return null;

  const typeData = Object.entries(data.published_by_type || {}).map(([type, count]) => ({
    name: ASSET_TYPE_LABELS[type] || type,
    count,
  }));

  const trendData = (data.weekly_trend || []).map(w => ({
    week: w.week ? new Date(w.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
    published: w.count,
  }));

  return (
    <Container fluid className="py-4" style={{ maxWidth: 1100 }}>
      <h3 style={{
        fontFamily: "'Trajan Pro', 'Times New Roman', serif",
        letterSpacing: '0.15em',
        color: colors.greyDarkest,
        marginBottom: 24,
      }}>
        WEEKLY REPORT
      </h3>

      {/* Summary cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <SummaryCard label="Published" value={data.total_published || 0} color={colors.goldDark} />
        </Col>
        <Col md={3}>
          <SummaryCard label="Pending Review" value={data.total_pending || 0} color="#ffc107" />
        </Col>
        <Col md={3}>
          <SummaryCard label="Expired" value={data.total_expired || 0} color={colors.greyMid} />
        </Col>
        <Col md={3}>
          <SummaryCard
            label="Top Clicks"
            value={data.top_performers?.[0]?.performance?.clicks || 0}
            color="#28a745"
          />
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4 g-3">
        <Col md={6}>
          <Card style={{ border: `1px solid ${colors.greyLight}` }}>
            <Card.Header style={{ background: colors.greyDarkest, color: colors.goldLight, fontFamily: "'Trajan Pro', 'Times New Roman', serif", letterSpacing: '0.1em', fontSize: 13 }}>
              PUBLISHED BY TYPE
            </Card.Header>
            <Card.Body>
              {typeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={typeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={colors.goldDark} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No published data yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{ border: `1px solid ${colors.greyLight}` }}>
            <Card.Header style={{ background: colors.greyDarkest, color: colors.goldLight, fontFamily: "'Trajan Pro', 'Times New Roman', serif", letterSpacing: '0.1em', fontSize: 13 }}>
              WEEK-OVER-WEEK TREND
            </Card.Header>
            <Card.Body>
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="published" stroke={colors.goldDark} strokeWidth={2} dot={{ fill: colors.goldDark }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No trend data yet</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top performers */}
      <Card style={{ border: `1px solid ${colors.greyLight}` }}>
        <Card.Header style={{ background: colors.greyDarkest, color: colors.goldLight, fontFamily: "'Trajan Pro', 'Times New Roman', serif", letterSpacing: '0.1em', fontSize: 13 }}>
          TOP PERFORMERS
        </Card.Header>
        <Card.Body className="p-0">
          {(data.top_performers || []).length > 0 ? (
            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '10px 16px' }}>Content</th>
                  <th style={{ padding: '10px 16px' }}>Type</th>
                  <th style={{ padding: '10px 16px' }}>Platform</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right' }}>Clicks</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {data.top_performers.map(asset => (
                  <tr key={asset.id}>
                    <td style={{ padding: '10px 16px', maxWidth: 400 }}>
                      {(asset.content?.title || asset.content?.text || '').slice(0, 80)}
                      {(asset.content?.title || asset.content?.text || '').length > 80 ? '...' : ''}
                    </td>
                    <td style={{ padding: '10px 16px' }}>{ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}</td>
                    <td style={{ padding: '10px 16px' }}>{(asset.target_platforms || []).join(', ')}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600 }}>
                      {asset.performance?.clicks || 0}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      {asset.topic_score?.toFixed(1) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted text-center py-4">No performance data yet. Publish assets and check back.</p>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <Card style={{ border: `1px solid ${colors.greyLight}`, textAlign: 'center' }}>
      <Card.Body className="py-3">
        <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: colors.greyMid, marginTop: 4 }}>{label}</div>
      </Card.Body>
    </Card>
  );
}

export default WeeklyReport;
